import { useEffect, useState } from "react";
import Key from "../Assets/Icons/key.svg";
import Vector from "../Assets/Images/Welcome.png";
import WeddinglyLogo from "../Assets/Images/logo-auth.svg";
import X_Circle from "../Assets/Icons/x-circle.svg";
import Cursor from "../Assets/Images/cursor-left.svg";
import { Link as RouterLink, Link, useNavigate } from "react-router-dom";
import MobileHeaderImage from "../Assets/Images/Mobile-logo-auth.svg";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "axios";

import Divider from "../Components/Divider";
import GoogleAuthButton from "../Components/GoogleAuthButton";
import TncText from "../Components/TncText";
import useTnC from "../Hooks/useTnC";

// Problem types look like "https://app.weddingly.io/problem/account-blocked";
// match on the path so a backend change to the host doesn't break these checks.
const isProblemType = (type, name) =>
  typeof type === "string" && type.endsWith(`/problem/${name}`);

const Login = (props) => {
  const navigate = useNavigate();
  const [Email, setEmail] = useState("");
  const [Password, setPassword] = useState("");
  const [ShowPassword, setShowPassword] = useState(false);
  const [UsernameValidation, setUsernameValidation] = useState({
    hasError: false,
    errorMessage: "",
  });
  const [PasswordValidation, setPasswordValidation] = useState({
    hasError: false,
    errorMessage: "",
  });

  const { tncHTML } = useTnC();

  const [tncOpen, setTncOpen] = useState(false);

  useEffect(() => {
    // Dynamically load the reCAPTCHA script
    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=6LemYw0qAAAAAPkmgvUN-XqqxnJZlBfXbCCU9A1l";
    script.async = true;
    script.defer = true;
    script.id = "recaptcha-script";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    // Remove chatId from session storage when user enter login page
    sessionStorage.removeItem("chatId");
    sessionStorage.removeItem("promptText");
  }, []);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setUsernameValidation({ hasError: false, errorMessage: "" });
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordValidation({ hasError: false, errorMessage: "" });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Handle form submission logic
  };

  const handleShowPassword = () => {
    setShowPassword(!ShowPassword);
  };

  const handleClearUsername = () => {
    setEmail("");
    setUsernameValidation({ hasError: false, errorMessage: "" });
  };

  const validateLoginEntries = () => {
    // Email (username) validation
    if (Email.length === 0) {
      setUsernameValidation({
        hasError: true,
        errorMessage: "Please enter your email.",
      });
    } else if (
      Email !== "admin" &&
      !/^[\w.+-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(Email)
    ) {
      setUsernameValidation({
        hasError: true,
        errorMessage: "Please enter a valid email address.",
      });
    } else {
      // Password validation
      if (Password.length === 0) {
        setPasswordValidation({
          hasError: true,
          errorMessage: "Please enter your password.",
        });
      } else {
        handleLogin();
      }
    }
  };

  const handleLogin = () => {
    // BaseURL can go missing mid-session (e.g. a logout in another tab calls
    // localStorage.clear()); fall back to the current origin rather than
    // building a broken "null/api/..." relative path.
    const baseURL =
      window.localStorage.getItem("BaseURL") || window.location.origin;
    let Req = {
      method: "POST",
      baseURL: baseURL + "/api/authenticate",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        username: Email,
        password: Password,
      },
    };

    axios(Req)
      .then((Res) => {
        console.log(Res);
        window.localStorage.setItem("Token", Res.data.id_token);
        // document.getElementById("link-to-home").click();
        window.location.assign("/Home");
      })
      .catch((Err) => {
        console.log(Err);
        if (!Err.response) {
          setUsernameValidation({
            hasError: true,
            errorMessage: "Unable to reach the server. Please try again.",
          });
          return;
        }
        if (Err.response.status === 401) {
          if (Err.response.data.detail === "ACCOUNT_LOCKED") {
            setPasswordValidation({
              hasError: true,
              errorMessage: "",
            });
            setUsernameValidation({
              hasError: true,
              errorMessage:
                "Multiple failed attempts. Account locked for 15 minutes.",
            });
          } else {
            // Wrong password error
            setPasswordValidation({
              hasError: true,
              errorMessage: "The username and password combination is invalid.",
            });
            setUsernameValidation({
              hasError: true,
              errorMessage: "The username and password combination is invalid.",
            });
          }
        } else if (
          Err.response.status === 500 &&
          Err.response.data.detail === "Invalid credentials"
        ) {
          setUsernameValidation({
            hasError: true,
            errorMessage: "Please verify your email and try again.",
          });
        } else if (Err.response.status === 403) {
          if (
            Err.response.data.detail ===
            "This account must set a new password before signing in."
          ) {
            navigate("/PasswordResetRequired", { state: { email: Email } });
          } else if (isProblemType(Err.response.data.type, "account-blocked")) {
            setUsernameValidation({
              hasError: true,
              errorMessage: "Your account has been blocked.",
            });
          } else if (isProblemType(Err.response.data.type, "account-locked")) {
            setUsernameValidation({
              hasError: true,
              errorMessage: "Your account has been locked.",
            });
          }
        } else if (
          Err.response.status === 429 &&
          isProblemType(Err.response.data.type, "too-many-requests")
        ) {
          const retryAfterSeconds = Err.response.headers["retry-after"];
          setUsernameValidation({
            hasError: true,
            errorMessage: retryAfterSeconds
              ? `Too many sign-in attempts. Please wait ${retryAfterSeconds} seconds and try again.`
              : "Too many sign-in attempts. Please wait and try again.",
          });
        } else {
          setUsernameValidation({
            hasError: true,
            errorMessage: "Something went wrong. Please try again.",
          });
        }
      });
  };

  const inputClasses = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "15px",
      backgroundColor: "#fff1f2",
      border: "none",
      transition: "background-color 0.3s ease-in-out", // Smooth transition for background
      minWidth: "90% !important", // Updated
      maxWidth: "500px !important", // Updated
      width: "100%",
      fontSize: "20px",
      fontWeight: "600",
      paddingLeft: "10px",
      color: "#171717",
    },

    "& .MuiOutlinedInput-root.Mui-focused": {
      backgroundColor: "#f8e0e0", // Change background on focus
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important", // Remove outline border
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },

    "& .MuiInputBase-input::placeholder": {
      color: "#1717174D", // Default placeholder color
      transition: "color 0.3s ease-in-out, opacity 0.3s ease-in-out", // Smooth placeholder animation
    },

    "& .MuiInputBase-input:focus::placeholder": {
      color: "#9e7676",
      opacity: 0.9,
    },
  };

  if (tncOpen) {
    return (
      <main className="auth-wrapper">
        <Link hidden id="link-to-home" to="/Home"></Link>
        <section className="auth-left-wrapper">
          <div className="auth-vector-container">
            <img className="auth-vector" alt="welcome" src={Vector} />
          </div>
          <img
            src={WeddinglyLogo}
            alt="weddingly logo"
            className="auth-weddingly-logo"
          />
        </section>
        <section className="auth-right-wrapper">
          <div className="section-head-action is-desktop">
            <Link to="/">
              <img alt="" src={Cursor} />
              <p>Back to landing page</p>
            </Link>
          </div>
          <form className="auth-form big-form" style={{ minWidth: "80%" }}>
            <p className="form-head-text form-head-text-small">
              Terms, Payment and Data Privacy
            </p>
            <div
              className="terms-text-wrapper  mobile-terms-wrapper"
              dangerouslySetInnerHTML={{ __html: tncHTML }}
            />
          </form>
          <span className="back-btn" onClick={() => setTncOpen(false)}>
            Back to login
          </span>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-wrapper">
      <Link hidden id="link-to-home" to="/Home"></Link>
      <section className="auth-left-wrapper">
        <div className="auth-vector-container">
          <img className="auth-vector" alt="welcome" src={Vector} />
        </div>
        <img
          src={WeddinglyLogo}
          alt="weddingly logo"
          className="auth-weddingly-logo"
        />
      </section>
      <section className="auth-right-wrapper">
        <div className="section-head-action is-desktop">
          <Link to="/">
            <img alt="" src={Cursor} />
            <p>Back to landing page</p>
          </Link>
        </div>
        <img
          alt=""
          className="is-mobile mobile-header-image"
          src={MobileHeaderImage}
        />
        <form className="auth-form" onSubmit={handleSubmit}>
          <p className="form-head-text">Welcome back</p>
          <TextField
            autoComplete="off" // Disable autofill
            fullWidth
            id="email"
            name="weddingly-username"
            variant="outlined"
            placeholder="Email"
            value={Email}
            onChange={handleEmailChange}
            error={UsernameValidation.hasError}
            helperText={UsernameValidation.errorMessage}
            margin="normal"
            sx={inputClasses}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {Email && ( // Render clear button only if username has value
                    <IconButton
                      aria-label="clear username"
                      onClick={handleClearUsername}
                      edge="end"
                    >
                      <img alt="x" src={X_Circle} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <TextField
            autoComplete="new-password" // Disable autofill
            fullWidth
            name="weddingly-password"
            id="password"
            placeholder="Password"
            type={ShowPassword ? "text" : "password"}
            variant="outlined"
            value={Password}
            onChange={handlePasswordChange}
            error={PasswordValidation.hasError}
            helperText={PasswordValidation.errorMessage}
            margin="normal"
            sx={{
              ...inputClasses,
              "& input[type='password']": {
                ...(Password.length && {
                  fontFamily: "Verdana",
                  letterSpacing: "2px",
                }),
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleShowPassword}
                    edge="end"
                    style={{ color: "#171717" }}
                  >
                    {ShowPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <section className="form-links">
            <Link
              component={RouterLink}
              to="/ResetPassword"
              variant="body2"
              sx={{ marginTop: "10px", display: "block" }}
            >
              <Typography color="#FB4764" fontSize={15}>
                Forgot password
              </Typography>
            </Link>
            <Link
              component={RouterLink}
              to={props.NewRegisterAllowed ? "/Signup" : undefined}
              variant="body2"
              sx={{
                marginTop: "10px",
                display: "block",
              }}
              style={
                props.NewRegisterAllowed
                  ? { opacity: "1" }
                  : { opacity: "0", pointerEvents: "none" }
              }
            >
              <Typography color="#FB4764" fontSize={15}>
                Sign up
              </Typography>
            </Link>
          </section>

          <Button
            type="submit"
            variant="contained"
            onClick={() => validateLoginEntries()}
            sx={{
              backgroundColor: "#FB4764",
              "&:hover": {
                backgroundColor: "#FB4764",
              },
              height: "56px",
              fontSize: "25px",
              fontWeight: "400",
              display: "flex",
              color: "#ffff",
              boxShadow: "none",
              marginTop: "16px",
              gridGap: "8px",
              borderRadius: "15px",
              textTransform: "none", // Prevent text from being all caps,
            }}
          >
            <img
              alt=""
              src={Key}
              style={{
                width: "30px",
                height: "30px",
              }}
            />
            Sign in
          </Button>
          <Divider />
          <GoogleAuthButton text="Sign in with Google" />
          <div style={{ marginTop: "20px" }}>
            <TncText onClick={() => setTncOpen(true)} />
          </div>
        </form>
      </section>
    </main>
  );
};

export default Login;
